import {
  ArrayField,
  ChipField,
  Datagrid,
  FunctionField,
  ReferenceArrayField,
  ReferenceField,
  SimpleShowLayout,
  SingleFieldList,
  TextField,
  useNotify,
  useResourceContext,
} from "react-admin";
import PolicyIcon from "@mui/icons-material/Policy";
import DownloadIcon from "@mui/icons-material/Download";
import { settings } from "app";
import LoadingActionButton from "server/components/LoadingActionButton";
import { getFile, getFileFromUrl, previewFileFromUrl } from "server/components/DownloadFile";
import { CampaignTypes, PreferenceOrigin } from "utils/constants";
import { candidateRequests } from "api/candidateApi";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import { Grid, Typography, useMediaQuery } from "@mui/material";
import AddIntermediaryDialog from "./AddIntermediaryDialog";
import ManageNoteDialog from "./ManageNoteDialog";
import PreferencesTab from "features/PreferencesTab";
import { documentRequests } from "api/documentsApi";
import { administrationRequests } from "api/administrationApi";

const CandidacyDeclarations = ({ record, isForeigner }) => {
  const notify = useNotify();
  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));

  const resourceContext = useResourceContext();
  const isRegularCampaign = resourceContext.includes("regular");

  const handlePreviewDeclaration = async dynamicDeclaration => {
    previewFileFromUrl(
      `${settings.gatewayApi}/${candidateRequests.getCandidacyDynamicDeclaration(record.id, dynamicDeclaration.id)}`,
      null,
      notify,
    );
  };

  const handleDownloadDeclaration = async dynamicDeclaration => {
    getFileFromUrl(
      `${settings.gatewayApi}/${candidateRequests.getCandidacyDynamicDeclaration(record.id, dynamicDeclaration.id)}`,
      null,
      notify,
    );
  };

  return (
    <SimpleShowLayout>
      <Grid
        container
        spacing={3}
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        {!isForeigner && (
          <Grid
            item
            xs={12}
          >
            <Typography
              fontWeight={600}
              sx={{ color: "#4E2683" }}
            >
              Декларации
            </Typography>
            <ArrayField
              source="availableDynamicDocumentIds"
              label="Декларации"
            >
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <SecuredComponent permission={permissions.canPreviewCandidateDeclarations}>
                  <FunctionField
                    render={rec => (
                      <>
                        <LoadingActionButton
                          label="Виж"
                          icon={<PolicyIcon />}
                          onClick={() => handlePreviewDeclaration(rec)}
                        />
                        <LoadingActionButton
                          label="Изтегли"
                          icon={<DownloadIcon />}
                          onClick={() => handleDownloadDeclaration(rec)}
                        />
                      </>
                    )}
                  />
                </SecuredComponent>
                <ReferenceField
                  sortable={false}
                  label="Декларация"
                  source="id"
                  reference={documentRequests.getDynamicDeclarationsSelect()}
                >
                  <TextField
                    label="Име"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
              </Datagrid>
            </ArrayField>
          </Grid>
        )}
        {record?.declarationDocuments?.length > 0 ? (
          <Grid
            item
            xs={12}
          >
            <Typography
              fontWeight={600}
              sx={{ color: "#4E2683" }}
            >
              Подадени декларации
            </Typography>
            <ArrayField
              source="declarationDocuments"
              label=""
            >
              <Datagrid
                rowClick={false}
                bulkActionButtons={false}
                sx={settings.style.secondaryDatagrid}
              >
                <ReferenceField
                  sortable={false}
                  label="Декларация"
                  source="declarationId"
                  reference={documentRequests.getDynamicDeclarationsSelect()}
                >
                  <TextField
                    label="Декларация"
                    source="name"
                    emptyText="-"
                  />
                </ReferenceField>
                <FunctionField
                  sortable={false}
                  label="Свали документ"
                  render={declarationDocument =>
                    declarationDocument.documentId ? (
                      <Typography
                        sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                        onClick={() => getFile(declarationDocument.documentId, notify ? notify : "")}
                      >
                        {`file-${declarationDocument.documentId}.pdf`}
                      </Typography>
                    ) : (
                      "-"
                    )
                  }
                />
              </Datagrid>
            </ArrayField>
          </Grid>
        ) : null}
        {record?.campaignTypeId !== CampaignTypes.PreparatoryCourses ? (
          <Grid
            item
            xs={12}
          >
            <PreferencesTab
              record={record}
              isForeigner={isForeigner}
              handleKey={PreferenceOrigin.Candidacy}
            />
            {isForeigner ? (
              <Grid
                item
                xs={12}
                sx={{ mb: 3 }}
              >
                <Typography
                  fontWeight={600}
                  sx={{ color: "#4E2683" }}
                >
                  Представители
                </Typography>

                <ReferenceArrayField
                  sortable={false}
                  source="intermediaryIds"
                  reference={administrationRequests.getIntermediariesSelect()}
                >
                  <SingleFieldList linkType={`/administration/intermediaries/${record?.intermediaryIds?.[0]}/edit`}>
                    <ChipField source="name" />
                  </SingleFieldList>
                  {!record?.intermediaryIds || record?.intermediaryIds?.length === 0 ? (
                    <Typography sx={{ fontSize: "14px" }}>Кандидатства лично</Typography>
                  ) : null}
                </ReferenceArrayField>
                <SecuredComponent permission={permissions.canAddIntermediariesToCandidacy}>
                  <AddIntermediaryDialog
                    candidacyId={record?.id}
                    disabled={record?.intermediaryIds?.length >= 1 || record?.hasApprovedRanking}
                  />
                </SecuredComponent>
              </Grid>
            ) : null}
            {!isForeigner && record?.laureateDocuments?.length > 0 ? (
              <>
                <Typography
                  fontWeight={600}
                  sx={{ color: "#4E2683" }}
                >
                  Лауреати
                </Typography>
                <ArrayField source="laureateDocuments">
                  <Datagrid
                    rowClick={false}
                    bulkActionButtons={false}
                    sx={{ ...settings.style.secondaryDatagrid, mb: 3 }}
                  >
                    <ReferenceField
                      sortable={false}
                      source="laureateId"
                      reference={administrationRequests.getLaureatesSelect()}
                      label="Лауреат"
                    >
                      <TextField source="name" />
                    </ReferenceField>
                    <FunctionField
                      sortable={false}
                      label={"Свали документ"}
                      render={record =>
                        record.documentId !== 0 ? (
                          <Typography
                            sx={{ textDecoration: "underline", fontSize: "14px", cursor: "pointer" }}
                            onClick={() => getFile(record.documentId, notify ? notify : "")}
                          >
                            {`file-${record.documentId}.pdf`}
                          </Typography>
                        ) : null
                      }
                    />
                  </Datagrid>
                </ArrayField>
              </>
            ) : null}
            {isRegularCampaign ? (
              <>
                <Grid
                  item
                  xs={12}
                >
                  <Typography
                    fontWeight={600}
                    sx={{ color: "#4E2683" }}
                  >
                    Бележки
                  </Typography>
                  <TextField
                    source="note.note"
                    emptyText="-"
                  />
                </Grid>
                <Grid item>
                  <SecuredComponent permission={permissions.canAddCandidacyNote}>
                    <ManageNoteDialog
                      disabled={record?.hasApprovedRanking}
                      candidacyId={record?.id}
                      currentNote={record?.note?.note}
                    />
                  </SecuredComponent>
                </Grid>
              </>
            ) : null}
          </Grid>
        ) : null}
      </Grid>
    </SimpleShowLayout>
  );
};

export default CandidacyDeclarations;
